"use client"

import { useClientTranslation } from "@/modules/App/components/i18n/i18n.client"
import { GuideOverlay } from "@/modules/Recap/components/GuideOverlay/guide-overlay"
import { useAnimalisContestPreview } from "@/shared/hooks/use-animalis-contest-preview"
import { useMounted } from "@/shared/hooks/use-mounted"
import { getStepNumber, ROUTES } from "@/shared/routing/routes"
import { isMultiPet } from "@/shared/routing/route-resolver"
import {
  ANIMALIS_CONTEST_TC_URL,
  isAnimalisContestBannerVisible,
} from "@/shared/utils/animalis-contest"
import getFRPromoBannerConfig from "@/shared/utils/banner"
import { isSendMyQuoteEligible } from "@/shared/utils/is-send-my-quote-eligible"
import { newSubscriptionFlowService } from "@/store/machine"
import { Banner, Skeleton, cn } from "@dalmatech/ui"
import { useActor } from "@xstate/react"
import dynamic from "next/dynamic"
import Image from "next/image"
import Link from "next/link"
import { usePathname } from "next/navigation"
import { usePostHog } from "posthog-js/react"
import { useEffect, useState } from "react"
import { Trans } from "react-i18next"

import { Breadcrumb } from "../Breadcrumb/breadcrumb"
import { SendMyQuote } from "../SendMyQuote/send-my-quote"
import * as styles from "./navbar.styled"

const DynamicRightPartOfNav = dynamic(
  () => import("./right-part-of-nav").then((mod) => mod.RightPartOfNav),
  {
    ssr: false,
  }
)

export const Navbar = () => {
  const isMounted = useMounted()
  const { t, i18n } = useClientTranslation("global")
  const posthog = usePostHog()
  const [state] = useActor(newSubscriptionFlowService)
  const pathname = usePathname()
  const promoCode = state.context.quote?.meta?.promo_code
  const rafCode = state.context.quote?.meta?.raf_code
  const quoteId = state.context.quote?.meta?.quote_id
  const userId = state.context.quote?.meta?.user_id
  const [isGuideOpen, setIsGuideOpen] = useState(false)
  const isGuideDisplayed =
    pathname === ROUTES.recap.path ||
    pathname === ROUTES.prepackages.path ||
    pathname === ROUTES.healthCoverageParam.path ||
    pathname === ROUTES.userBirthInformations.path ||
    pathname === ROUTES.tribeQuote.path ||
    pathname === ROUTES.userInformations.path

  const isSendMyQuoteVisible =
    isSendMyQuoteEligible(pathname) && !!state.context.quote?.meta?.quote_id

  const bannerConfig = getFRPromoBannerConfig(promoCode, rafCode)
  const isPromoBannerVisible = Boolean(bannerConfig && (rafCode || promoCode))
  const isContestPreview = useAnimalisContestPreview()
  // Contest banner is subordinate to the promo/RAF banner (DLM-1775 precedence rule).
  const isContestBannerVisible =
    !isPromoBannerVisible &&
    isAnimalisContestBannerVisible(state.context.quote, undefined, {
      forceActiveWindow: isContestPreview,
    })
  useEffect(() => {
    if (bannerConfig) {
      const id = requestAnimationFrame(() => {
        return () => cancelAnimationFrame(id)
      })
    }
  }, [bannerConfig])
  const completedStepsBarWidth = (getStepNumber(pathname) / 14) * 100
  const isPrepackagesPage = pathname === ROUTES.prepackages.path
  const isHealthCoverageParamPage = pathname.includes(ROUTES.healthCoverageParam.path)
  const showMultiPetPromoBanner =
    (isPrepackagesPage || isHealthCoverageParamPage) && isMultiPet(state.context.quote)

  return (
    <div className={styles.container}>
      {isPromoBannerVisible ? (
        <Banner
          title={
            <Trans
              t={t}
              i18nKey={bannerConfig?.i18nKey || ""}
              values={bannerConfig?.values}
              components={{ strong: <strong /> }}
            />
          }
        />
      ) : isContestBannerVisible ? (
        <Banner
          title={
            <Trans
              t={t}
              i18nKey="banner.contest.animalis-bts"
              components={{
                // NB: the tag name must NOT be an HTML void element (br, link, img…).
                // react-i18next's parser (html-parse-stringify + void-elements) would
                // self-close it and drop the label out of the anchor → non-clickable.
                rules: (
                  <Link
                    href={new URL(ANIMALIS_CONTEST_TC_URL)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline"
                  />
                ),
              }}
            />
          }
        />
      ) : null}
      <div className={styles.root}>
        <div className={styles.navbar}>
          <div className="flex justify-start">
            <DynamicRightPartOfNav />
          </div>
          <div className="flex justify-center">
            {isMounted ? (
              <Breadcrumb className={styles.breadcrumb} />
            ) : (
              <Skeleton className={styles.skeleton} />
            )}
          </div>
          <div className="flex justify-end gap-2">
            {isGuideDisplayed ? (
              <button
                onClick={() => {
                 // add tracking for CTA
                  posthog?.capture("cta_clicked", {
                    CTA_name: "guide",
                    quote_id: quoteId,
                    user_id: userId,
                    locale: i18n.language,
                  })
                  setIsGuideOpen(true)
                }}
                className="min-h-10 flex h-10 max-h-10 shrink-0 cursor-pointer items-center justify-center gap-2 rounded-full bg-neutral-100 px-4 py-3"
              >
                <Image src="/img/illus/book-open.svg" alt="" aria-hidden width={20} height={20} />{" "}
                <span className="whitespace-nowrap font-buenosAires text-sm font-bold leading-5 tracking-tight text-neutral-700">
                  {t("navbar.guide")}
                </span>
              </button>
            ) : null}
            {isSendMyQuoteVisible ? <SendMyQuote /> : null}
          </div>
        </div>
      </div>

      <div className={cn(styles.progressBar, "lg:hidden")}>
        <div
          className={styles.completedStepsBar}
          style={{ width: `${completedStepsBarWidth}%`, transition: "all 0.3s ease" }}
        />
        <div className={styles.activeStep}>
          <div className={styles.activeStepIndicator} />
        </div>
        <div className={styles.uncompletedStepsBar} />
      </div>
      {showMultiPetPromoBanner ? (
        <div className={styles.multiPetPromoBanner} role="banner">
          {t("navbar.multi-pet-promo-banner")}
        </div>
      ) : null}
      {isGuideDisplayed ? (
        <GuideOverlay
          isOpen={isGuideOpen}
          onClose={() => setIsGuideOpen(false)}
          rcContractId={state.context.rc_contract_id}
        />
      ) : null}
    </div>
  )
}
